import mixpanel from "mixpanel-browser"
import { useCallback } from "react"

import { appStore } from "../store/AppStore"
import { isTicketValid } from "../utilities/common"
import {
  locale,
  Platform,
  platform,
  windowLocale,
} from "../utilities/constants"
import {
  DeviceInfo,
  generateGenericId,
  getClientDeviceId,
  getDeviceOSInfo,
} from "../utilities/device-info"
import { captureSentryException } from "../utilities/sentry"

export const useMixpanel = () => {
  const tracking = appStore((state) => state.tracking)
  const currentUser = appStore((state) => state.currentUser)
  const hasTicket = isTicketValid(currentUser?.ticket?.validUntil)
  const geoInfo = appStore((state) => state.geoInfo)

  return useCallback(
    async (...trackArgs: Parameters<(typeof mixpanel)["track"]>) => {
      if (!tracking.mixpanel) return

      let deviceInfo: DeviceInfo | undefined
      try {
        deviceInfo = await getDeviceOSInfo()
      } catch (e) {
        console.log(e)
        captureSentryException(e)
      }
      let deviceId: string | undefined
      try {
        deviceId = await getClientDeviceId()
      } catch (e) {
        console.log(e)
        captureSentryException(e)
      }
      if (!deviceId)
        deviceId = "ERROR GENERATING NATIVE ID: " + generateGenericId()

      const args = trackArgs
      // global properties
      const globals = {
        user_id: currentUser?.id,
        user_type: currentUser
          ? currentUser.ticket?.isTrial
            ? "Trial"
            : hasTicket
              ? "Subscribed"
              : "Registered"
          : "Guest",
        data_source: "SmartTV",
        device_manufacturer:
          platform === Platform.WebOS
            ? "LG"
            : platform === Platform.Tizen
              ? "Samsung"
              : platform === Platform.Chromecast
                ? "Google"
                : "Web",
        smarttv_os: platform,
        smarttv_device_id: deviceId,
        smarttv_model_name: deviceInfo?.modelName,
        smarttv_sdk_version: deviceInfo?.sdkVersion,
        smarttv_firmware_version: deviceInfo?.firmwareVersion,
        smarttv_language: windowLocale,
        ui_language: locale?.toUpperCase(),
        time: Date.now(),
        country: geoInfo?.countryCode,
      }
      // merge globals with track input
      args[1] = { ...args[1], ...globals }
      mixpanel.track(...args)
    },
    [tracking, currentUser],
  )
}
